import { css, type Theme } from '@emotion/react';

import { Button, Flex } from '@toolbox/components';
import { CloseIcon } from '@toolbox/components/Icon/icons/CloseIcon';
import { useCloseWidget, useFlags, useLeadData, useWidgetContext } from '@widget/core';
import { richTextResolver } from '../../storyblok/rteResolver';
import { CloseConfirmationModal } from '../CloseConfirmationModal';
import { TrustIcons } from '../TrustIcons';
import { LAYOUT_HEADER_TYPE } from '@widget/storyblok/contants';
import { pushGAEvent } from '@widget/analytics';
import { getAnalyticsFields } from '@widget/utils/getAnalyticsFields';

import type { ImageAsset, LeadData, TrustIcon } from '@widget/core';
import type { Richtext } from 'storyblok-js-client';

type Props = {
  showValuation: boolean;
  closeText?: string | null;
  marketingHeaderText?: Richtext | string;
  valuation: null | string;
  onClickLogo?: () => void;
  logo?: ImageAsset;
  closeModalTitle?: string | null;
  closeModalSubtitle?: Richtext | string | null;
  closeModalCloseModalText?: string | null;
  closeModalCloseWidgetText?: string | null;
  trustIconsList?: TrustIcon[] | null;
  showCloseIcon?: boolean;
};

const classes = {
  header: (theme: Theme) => css`
    width: 100%;
    justify-content: space-between;

    ${theme.media.up('sm')} {
      flex-direction: row;
      align-items: center;
    }
  `,
  textContainer: (theme: Theme) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    column-gap: 2rem;
    ${theme.media.down('sm')} {
      flex-direction: column;
      align-items: flex-start;
    }
  `,
  valuation: css`
    text-align: center;
  `,
  progressBar: css`
    position: sticky;
    top: 0;
  `,
  logo:
    ({ valuation }: { valuation: boolean }) =>
    () =>
      css`
        display: ${valuation ? 'none' : 'flex'};
        align-items: center;
        padding: 0;
        appearance: none;
        border: none;
        box-shadow: none;
        background: transparent;
        min-height: 0;

        &:hover {
          cursor: pointer;
        }
      `,
  closeBtnText: (theme: Theme) => css`
    ${theme.media.down('sm')} {
      display: none;
    }
  `,
  headerText: (theme: Theme) => css`
    width: 100%;
    font-size: ${theme.fontSizes.f1};
    line-height: ${theme.lineHeights.xs};

    p {
      margin: 0;
    }

    ${theme.media.up('sm')} {
      width: fit-content;
      font-size: ${theme.fontSizes.f4};
    }
  `,
  trustIconsContainer: (theme: Theme) => css`
    display: flex;
    align-items: center;
    gap: 40px;

    img {
      max-width: 100%;
    }

    ${theme.media.down('sm')} {
      gap: 16px;

      img {
        max-width: 80px;
      }
    }
  `,
  rightSide: (theme: Theme) => css`
    display: flex;
    align-items: center;
    gap: 80px;

    ${theme.media.down('sm')} {
      gap: 18px;
    }
  `,
};

export const HeaderV3 = ({
  showValuation,
  marketingHeaderText,
  logo,
  valuation,
  closeText,
  closeModalTitle,
  closeModalCloseModalText,
  closeModalCloseWidgetText,
  closeModalSubtitle,
  trustIconsList,
  showCloseIcon,
}: Props): JSX.Element => {
  const {
    close,
    layoutSettings,
    activeId,
    getStepProps,
    transactionId,
    widgetId,
    stepIndex,
    prevStepId,
    analyticsMap,
    getFormData,
  } = useWidgetContext();
  const { setIsOpenModal, isThankYouPageShown } = useCloseWidget();
  const { setLeadData } = useLeadData();
  const { enableExitPopup } = useFlags();
  const stepProps = getStepProps(activeId);
  const step = getStepProps(activeId);

  const headerText =
    step?.headerType === LAYOUT_HEADER_TYPE.SOCIAL_PROOF_MESSAGE && step?.socialProofMessage
      ? step.socialProofMessage
      : marketingHeaderText &&
        !layoutSettings?.hideHeaderText &&
        step?.headerType === LAYOUT_HEADER_TYPE.DEFAULT
      ? marketingHeaderText
      : null;

  const closeWidget = (): void => {
    if (!isThankYouPageShown && enableExitPopup) {
      setIsOpenModal(true);
    } else {
      setLeadData({} as LeadData);
      close();
    }
  };

  const onLogoClick = () => {
    pushGAEvent('step-click_on_logo', {
      ...getAnalyticsFields({ analyticsMap, source: getFormData() }),
      transactionId,
      widgetId,
      stepIndex,
      stepId: activeId,
      prevStepId,
    });

    closeWidget();
  };

  const onCloseButtonClick = () => {
    pushGAEvent('step-click_to_close_widget', {
      ...getAnalyticsFields({ analyticsMap, source: getFormData() }),
      transactionId,
      widgetId,
      stepIndex,
      stepId: activeId,
      prevStepId,
    });

    closeWidget();
  };

  return (
    <Flex alignItems="center" css={classes.header as any}>
      <div css={classes.textContainer as any}>
        <button
          css={classes.logo({ valuation: !!valuation && showValuation }) as any}
          onClick={onLogoClick}
          aria-label="Close widget"
        >
          {typeof logo === 'object' && logo?.filename && (
            <img
              src={logo?.filename ?? 'https://a.storyblok.com/f/122189/x/1f71466826/logo.svg'}
              alt={logo?.alt ?? ''}
            />
          )}
        </button>

        {/* {showValuation && (
        <div>
          <ValuationText />
        </div>
      )} */}
        {headerText &&
          !layoutSettings?.hideHeaderText &&
          stepProps?.headerType !== LAYOUT_HEADER_TYPE.TRUST_ELEMENTS && (
            <div
              css={classes.headerText as any}
              dangerouslySetInnerHTML={{
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                __html:
                  typeof headerText == 'string' ? headerText : richTextResolver.render(headerText),
              }}
            />
          )}
      </div>
      <div css={classes.rightSide as any}>
        {trustIconsList && stepProps?.headerType === LAYOUT_HEADER_TYPE.TRUST_ELEMENTS && (
          <div css={classes.trustIconsContainer as any}>
            <TrustIcons trustIconsList={trustIconsList} autoSize height="40px" />
          </div>
        )}
        {showCloseIcon && (
          <Button onClick={onCloseButtonClick} size={'medium'} variant={'tertiary'}>
            <span css={classes.closeBtnText as any}>{closeText}</span>{' '}
            <CloseIcon css={{ width: 13 } as any} />
          </Button>
        )}
      </div>
      <CloseConfirmationModal
        title={closeModalTitle}
        subtitle={closeModalSubtitle}
        closeModalText={closeModalCloseModalText}
        closeWidgetText={closeModalCloseWidgetText}
      />
    </Flex>
  );
};
